import { JSX, useMemo } from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  HeaderContext,
} from "react-table-8.10.7";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { RecognitionWaterfallPeriod } from "store/apis/recognitionWaterfall";
import { PRIOR_HINT, WaterfallLabels } from "./constants";
import { NameCell } from "./NameCell";
import {
  COLUMN_KIND,
  getColumnAmount,
  getColumnTitle,
  ROW_TYPE,
  WaterfallColumn,
  WaterfallRow,
} from "./waterfallRows";

export type SelectCell = (
  row: WaterfallRow,
  column: WaterfallColumn,
  cellId: string
) => void;

type AmountCellProps = {
  context: CellContext<WaterfallRow, unknown>;
  column: WaterfallColumn;
  columnTitle: string;
  onSelectCell: SelectCell;
};
const AMOUNT_CELL_CLASS = "t-text-right t-pr-5";

const AmountCell = ({
  context,
  column,
  columnTitle,
  onSelectCell,
}: AmountCellProps): JSX.Element => {
  const row = context.row.original;
  const amount = getColumnAmount(row.amounts, column);
  const amountText = <AmountSuperScript amount={amount} />;

  // Only a customer/vendor cell with something in it has invoices to show
  if (row.type !== ROW_TYPE.PARTY || !amount) {
    return <div className={AMOUNT_CELL_CLASS}>{amountText}</div>;
  }

  const handleSelectCell = () => onSelectCell(row, column, context.cell.id);

  // Neutral link so the amount reads as plain text, as in the Revenue Waterfall
  return (
    <div className={AMOUNT_CELL_CLASS}>
      <Button
        customType="link_neutral"
        size="small"
        onClick={handleSelectCell}
        aria-label={`${row.label}, ${columnTitle}`}
      >
        {amountText}
      </Button>
    </div>
  );
};

const columnHelper = createColumnHelper<WaterfallRow>();

const sortableHeader =
  (title: string, align: "left" | "right", infoText?: string) =>
  ({ column }: HeaderContext<WaterfallRow, unknown>) =>
    (
      <div className="t-group t-whitespace-nowrap">
        <HeadCellContent
          title={title}
          align={align}
          infoText={infoText}
          sort={{
            nextSortOrder: column.getNextSortingOrder(),
            isSorted: column.getIsSorted(),
            sortingHandler: column.getToggleSortingHandler(),
          }}
        />
      </div>
    );

const COLUMN_SIZE: Record<WaterfallColumn["kind"], number> = {
  [COLUMN_KIND.PRIOR]: 160,
  [COLUMN_KIND.PERIOD]: 120,
  [COLUMN_KIND.DEFERRED]: 140,
};

// Amounts sort largest first on the first click
const AMOUNT_SORTING = { enableSorting: true, sortDescFirst: true };

const amountColumn = (
  id: string,
  column: WaterfallColumn,
  periods: RecognitionWaterfallPeriod[],
  onSelectCell: SelectCell,
  infoText?: string
): ColumnDef<WaterfallRow, number> => {
  const title = getColumnTitle(column, periods);
  return columnHelper.accessor((row) => getColumnAmount(row.amounts, column), {
    id,
    header: sortableHeader(title, "right", infoText),
    ...AMOUNT_SORTING,
    size: COLUMN_SIZE[column.kind],
    cell: (context) => (
      <AmountCell
        context={context}
        column={column}
        columnTitle={title}
        onSelectCell={onSelectCell}
      />
    ),
  });
};

export const useWaterfallColumns = (
  periods: RecognitionWaterfallPeriod[],
  labels: WaterfallLabels,
  onSelectCell: SelectCell
) =>
  useMemo(
    () => [
      columnHelper.accessor("label", {
        id: "name",
        header: sortableHeader(labels.party, "left"),
        enableSorting: true,
        sortDescFirst: false,
        size: 280,
        cell: ({ row }) => <NameCell row={row.original} />,
      }),
      amountColumn(
        "prior_recognized",
        { kind: COLUMN_KIND.PRIOR },
        periods,
        onSelectCell,
        PRIOR_HINT
      ),
      ...periods.map((_, index) =>
        amountColumn(
          `period_${index}`,
          { kind: COLUMN_KIND.PERIOD, index },
          periods,
          onSelectCell
        )
      ),
      amountColumn(
        "deferred",
        { kind: COLUMN_KIND.DEFERRED },
        periods,
        onSelectCell
      ),
      columnHelper.accessor("amounts.scheduled", {
        id: "scheduled",
        header: sortableHeader("Total scheduled", "right"),
        ...AMOUNT_SORTING,
        size: 140,
        cell: ({ getValue }) => (
          <div className={AMOUNT_CELL_CLASS}>
            <AmountSuperScript amount={Number(getValue())} />
          </div>
        ),
      }),
    ],
    [periods, labels, onSelectCell]
  );
